
import {
  computed, defineComponent, onBeforeMount, onMounted, onUnmounted, Ref, ref, nextTick, provide,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
import { Lazy, Mousewheel, Navigation } from 'swiper';
import { onClickOutside } from '@vueuse/core';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';
import DriveCropper from '@/modules/drive/components/desktop/mediaview/DriveCropper.vue';
import MediaSettingsPopUp from '@/components/BottomPopUp/PopUpTypes/Settings/MediaSettingsPopUp.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import DriveMediaViewSlide from '@/modules/drive/components/desktop/mediaview/DriveMediaViewSlide.vue';
import DriveMediaViewHeader from '@/modules/drive/components/desktop/mediaview/DriveMediaViewHeader.vue';
import DriveMediaViewNav from '@/modules/drive/components/desktop/mediaview/DriveMediaViewNav.vue';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import AppLabelInput from '@/components/UI/AppLabelInput.vue';
import AppSkeletonRect from '@/components/skeletons/AppSkeletonRect.vue';
import AppRenameFile from '@/components/AppRenameFile.vue';

import { ITEMS_ON_PAGE } from '@/types/consts';

import {
  BOTTOM_POPUP_TYPES, MEDIA_TYPES, NAVIGATION_PAGES, SHARE_TYPES,
} from '@/types/enums';
import {
  ICaseFilesManage,
  ICropCoordinates,
  IImageCrop,
  ILabelInput,
  IMedia,
  // ISharedLable,
  ITagInput,
} from '@/types/interfaces';
import clearPhotoCache from '@/helpers/clearPhotoCache';
import UploadService from '@/services/UploadService';

import 'swiper/swiper-bundle.css';
import DriveService from '@/modules/drive/services/DriveService';

export default defineComponent({
  components: {
    DriveCropper,
    MediaSettingsPopUp,
    SharePopUp,
    Swiper,
    SwiperSlide,
    DriveMediaViewSlide,
    DriveMediaViewHeader,
    DriveMediaViewNav,
    AppQuestionPopUp,
    AppLabelInput,
    AppSkeletonRect,
    AppRenameFile,
  },
  emits: ['on-close', 'close'],
  props: {
    viewOnly: { type: Boolean, default: false },
  },
  setup() {
    const mySwiper = ref();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const alertsStore = useAlertsStore();
    const router = useRouter();
    const route = useRoute();

    const isMixedView = ref(false);
    const initialSlide = ref(0);
    const currentMedia = ref(0);
    const showSkeleton = ref(false);
    const pauseVideo = ref(false);

    const isMediaSettingsPopUp = ref(false);
    provide('isMediaSettingsPopUp', isMediaSettingsPopUp);
    const isSharePopUp = ref(false);
    const isMoveToPopUp = ref(false);
    const isQuestionPopUp: Ref<boolean> = ref(false);
    const fileRenamePopUp = ref(false);
    const renameMountErrorInfo = ref<undefined | {title: string, error: string}>();

    const updateMediaOrderContentIds = ref<string[]>([]);

    // Label popup
    const labelPopup = ref();
    const showAddLabel = ref(false);
    function addLabelsOpen() {
      if (driveStore.appPhotoViewMedia?.labels?.length) {
        mainStore.inputPhotoTags = driveStore.appPhotoViewMedia?.labels as unknown as ITagInput[];
      }
      showAddLabel.value = true;
    }

    function updateMediaOrder(contentId: string) {
      const photoInPhotoList = driveStore.photos.data.find((photo) => photo.contentId === contentId);
      if (photoInPhotoList) {
        driveStore.photos.data = [photoInPhotoList, ...driveStore.photos.data.filter((photo) => photo.contentId !== contentId)];
      }

      driveStore.cases.data.map((cas) => {
        const photoInCase = cas.photos.find((photo) => photo.contentId === contentId);
        if (photoInCase) {
          return ({
            ...cas,
            photos: [photoInCase, ...cas.photos.filter((photo) => photo.contentId !== contentId)],
          });
        }
        const fileInCase = cas.files.find((file) => file.contentId === contentId);
        if (fileInCase) {
          return ({
            ...cas,
            files: [fileInCase, ...cas.files.filter((photo) => photo.contentId !== contentId)],
          });
        }
        return cas;
      });

      if (driveStore.currentCase) {
        const photoInCase = driveStore.currentCase.photos.find((ph) => ph.contentId === contentId);
        if (photoInCase) {
          driveStore.currentCase.photos = [photoInCase, ...driveStore.currentCase.photos.filter((photo) => photo.contentId !== contentId)];
        }
        const fileInCase = driveStore.currentCase.files.find((ph) => ph.contentId === contentId);
        if (fileInCase) {
          driveStore.currentCase.files = [fileInCase, ...driveStore.currentCase.files.filter((photo) => photo.contentId !== contentId)];
        }
      }

      if (driveStore.currentFolder && driveStore.currentFolder.mixedData) {
        const mediaInFolder = driveStore.currentFolder.mixedData.find((mix) => mix.file?.contentId === contentId);
        if (mediaInFolder) {
          driveStore.currentFolder.mixedData = [mediaInFolder, ...driveStore.currentFolder.mixedData.filter((mix) => mix.file?.contentId !== contentId)];
        }
      }

      const mediaInMixed = driveStore.mixedData.data.find((mix) => mix.file?.contentId === contentId);
      if (mediaInMixed) {
        driveStore.mixedData.data = [mediaInMixed, ...driveStore.mixedData.data.filter((mix) => mix.file?.contentId !== contentId)];
      }
    }

    function updateMediaLabels(labels: ILabelInput[]) {
      const { contentId } = driveStore.appPhotoViewMedia!;
      updateMediaOrderContentIds.value.push(contentId);

      const photoInPhotoList = driveStore.photos.data.find((photo) => photo.contentId === contentId);
      if (photoInPhotoList) {
        photoInPhotoList.labels = labels;
      }

      driveStore.cases.data.forEach((cas) => {
        const photoInCase = cas.photos.find((photo) => photo.contentId === contentId);
        if (photoInCase) {
          photoInCase.labels = labels;
        }
        const fileInCase = cas.files.find((file) => file.contentId === contentId);
        if (fileInCase) {
          fileInCase.labels = labels;
        }
      });

      if (driveStore.currentCase) {
        const photoInCase = driveStore.currentCase.photos.find((ph) => ph.contentId === contentId);
        if (photoInCase) {
          photoInCase.labels = labels;
          driveStore.cases.data = [driveStore.currentCase, ...driveStore.cases.data.filter((cas) => cas.id !== driveStore.currentCase.id)];
        }
        const fileInCase = driveStore.currentCase.files.find((ph) => ph.contentId === contentId);
        if (fileInCase) {
          fileInCase.labels = labels;
          driveStore.cases.data = [driveStore.currentCase, ...driveStore.cases.data.filter((cas) => cas.id !== driveStore.currentCase.id)];
        }
      }

      if (driveStore.currentFolder && driveStore.currentFolder.mixedData) {
        const mediaInFolder = driveStore.currentFolder.mixedData.find((mix) => mix.file?.contentId === contentId);
        if (mediaInFolder) {
          mediaInFolder.file!.labels = labels;
        }
      }

      const mediaInMixed = driveStore.mixedData.data.find((mix) => mix.file?.contentId === contentId);
      if (mediaInMixed) {
        mediaInMixed.file!.labels = labels;
      }
    }
    function getPosition() {
      console.log(123);
    }

    function addLabelsClose() {
      const ids = [] as number[];
      mainStore.inputPhotoViewTags.forEach((el) => {
        const elm = mainStore.recentInputLabels.find(({ name }) => name === el.text);
        if (elm) { ids.push(elm.id); }
      });
      const payload = {
        id: driveStore.appPhotoViewMedia?.id as string,
        labelIds: ids,
        photoCrop: null,
      };
      showAddLabel.value = false;
      DriveService.addImageLabels(payload).then((response) => {
        if (response.success) {
          const { labels } = response.results.data;
          driveStore.appPhotoViewMedia!.labels = labels;
          updateMediaLabels(labels);
          mainStore.inputPhotoTags = [];
        }
      }).finally(() => {
        mainStore.inputPhotoTags = [];
        showAddLabel.value = false;
      });
    }

    function cancelLabelsPopup() {
      mainStore.inputPhotoTags = [];
      showAddLabel.value = false;
    }

    function openLabelsPopup() {
      // if (driveStore.appPhotoViewMedia?.labels?.length) {
      //   mainStore.inputPhotoTags = driveStore.appPhotoViewMedia?.labels as unknown as ITagInput[];
      // }
      showAddLabel.value = true;
    }

    onClickOutside(labelPopup, cancelLabelsPopup);
    // -- end -- Label popup

    const isNetwork = computed(() => route.meta.isNetwork || false);
    const navigationPage = computed(() => route.meta.navigationPage);
    const isCase = computed(
      () => navigationPage.value === NAVIGATION_PAGES.DRIVE_CASES
        || navigationPage.value === NAVIGATION_PAGES.NETWORK_CASES
        || navigationPage.value === NAVIGATION_PAGES.CASE_EDIT
        || navigationPage.value === NAVIGATION_PAGES.CASE_VIEW,
    );

    const currentPage = ref(0);
    const CURRENT_ROUTE_META = computed(() => route.meta.navigationPage || 'no value');

    const mediaList = computed(() => {
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_PHOTOS || route.meta.navigationPage === NAVIGATION_PAGES.DRIVE || route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FILES) {
        return isMixedView.value ? driveStore.mixedData.data.filter((el) => el.type === 'FILE').map((el) => el.file) : driveStore.photos.data;
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.NETWORK || route.meta.navigationPage === NAVIGATION_PAGES.NETWORK_PHOTOS || route.meta.navigationPage === NAVIGATION_PAGES.NETWORK_CASES) {
        if (isCase.value && driveStore.currentCase.id) {
          return [...driveStore.photoList, ...driveStore.currentCase.files] || [];
        }
        return isMixedView.value
          ? driveStore.networkMixedData.data.filter((el) => el.type === 'FILE').map((el) => el.file)
          : driveStore.networkPhotos.data;
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FOLDER) {
        return driveStore.currentFolder?.mixedData?.filter((el) => el.type === 'FILE').map((el) => el.file) || [];
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.ALERTS) {
        return alertsStore.notifications.data.filter((el) => el.type === 'SHARE_FILE' && el.sharedInfo !== null).map((el) => el.sharedInfo);
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.CASE_VIEW || route.meta.navigationPage === NAVIGATION_PAGES.CASE_EDIT) {
        return [...driveStore.currentCase.photos, ...driveStore.currentCase.files];
      }
      const isMessagePage = (route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_ID || route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_GROUP_CHAT_ID);
      if (isMessagePage && driveStore.currentCase.id) {
        return [...driveStore.currentCase.photos, ...driveStore.currentCase.files];
      }
      return [driveStore.appPhotoViewMedia];
    });
    const currentItem = computed(() => driveStore.appPhotoViewMedia);
    const currentItemType = computed(() => (currentItem.value?.type === MEDIA_TYPES.PHOTO ? MEDIA_TYPES.PHOTO : MEDIA_TYPES.FILES));
    const isPhoto = computed(() => currentItem.value?.type === MEDIA_TYPES.PHOTO);
    const isFile = computed(() => currentItem.value?.type === MEDIA_TYPES.FILES);

    function setCurrentPage() {
      currentPage.value = Math.floor(mediaList.value.length / ITEMS_ON_PAGE) - 1;
    }

    function setItemShared() {
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // if (currentItemType.value === SHARE_TYPES.PHOTO) {
      //   driveStore.fullscreenPhoto!.shared = true;
      // }
      // if (currentItemType.value === SHARE_TYPES.FILE) {
      //   driveStore.fullscreenFile!.shared = true;
      // }
    }

    function setSharedLabels() { // labels: ISharedLable[]
      // is declared but it is not using now. Current code will not work correctly with new app logic
      // driveStore.fullscreenPhoto!.sharedLabels = labels;
    }

    const onSwiper = (swiper) => {
      const startMediaId = driveStore.appPhotoViewMedia?.contentId;

      let indexToGo = 0;
      if (startMediaId) {
        indexToGo = mediaList.value.map((el) => (el as IMedia)!.contentId).indexOf(startMediaId);
      }
      initialSlide.value = indexToGo;
      swiper.slideTo(indexToGo, 0, false);
    };

    const onSlideChange = (swiper) => {
      pauseVideo.value = true;
      const mediaType = (mediaList?.value[swiper.activeIndex] as IMedia)?.type;

      const mediaId = mediaList?.value[swiper.activeIndex]?.id;
      if (!mediaId || !mediaType) return;
      if (mediaType === MEDIA_TYPES.PHOTO) {
        // fetchPhoto(mediaId);
      }
      if (mediaType === MEDIA_TYPES.VIDEO) {
        // fetchPhoto(mediaId);
      }
      if (mediaType === MEDIA_TYPES.FILES) {
        // fetchFile(mediaId);
      }
      currentMedia.value = swiper.activeIndex;
      driveStore.appPhotoViewMedia = mediaList?.value[swiper.activeIndex] as IMedia;
      showAddLabel.value = false;
    };

    const notLoading = ref(false);
    function fetchData() {
      const resolveRequest = async () => {
        if (isNetwork.value) {
          return driveStore.fetchNetworkPhotos();
        }
        return driveStore.fetchPhotos();
      };
      return resolveRequest().then((response) => {
        driveStore.photoList = response;
        setCurrentPage();
        notLoading.value = false;
      });
    }

    const onReachEnd = (swiper) => {
      if (isCase.value && driveStore.currentCase.id) return;
      if (swiper.progress > 0 && !notLoading.value) {
        notLoading.value = true;
        if (currentPage.value < driveStore.photos.lastPage) {
          fetchData();
        }
      }
    };

    function openPopUp(type: BOTTOM_POPUP_TYPES) {
      if (isCase.value && isSharePopUp.value) {
        return;
      }
      mainStore.BottomPopUpType = type;
    }

    function openMovePopUp() {
      isMediaSettingsPopUp.value = false;
      isMoveToPopUp.value = true;
    }

    function openQuestionPopUp() {
      isMediaSettingsPopUp.value = false;
      isQuestionPopUp.value = true;
    }

    function deletePhoto() {
      if (isCase.value) {
        const removeFilePayload = {
          id: driveStore.currentCase.id!,
          fileIds: [driveStore.appPhotoViewMedia!.id],
        } as ICaseFilesManage;

        let idx = driveStore.photoList.findIndex((photo) => photo.id === driveStore.appPhotoViewMedia?.id);
        driveStore.removeFilesFromCase(removeFilePayload);

        if (driveStore.photoList.length >= 1) {
          idx = (idx + 1) % driveStore.photoList.length;
          driveStore.appPhotoViewMedia = driveStore.photoList[idx];
          isMediaSettingsPopUp.value = false;
        } else {
          driveStore.appPhotoViewMedia = undefined;
        }
      } else {
        isQuestionPopUp.value = false;
        driveStore.deleteMedia(driveStore.appPhotoViewMedia!.id, MEDIA_TYPES.PHOTO);
        driveStore.appPhotoViewMedia = undefined;
      }
    }

    function saveMediaAsCopy() {
      showSkeleton.value = true;
      isMediaSettingsPopUp.value = false;
      driveStore.saveMediaAsCopy(driveStore.appPhotoViewMedia!.id).then((response) => {
        if (response.success) {
          driveStore.appPhotoViewMedia = undefined;
          if (isNetwork.value) {
            router.push('/drive').then(() => {
              nextTick(() => {
                driveStore.appPhotoViewMedia = { ...response.results.data as IMedia, isMixed: true };
                showSkeleton.value = false;
              });
            });
          } else {
            nextTick(() => {
              driveStore.appPhotoViewMedia = { ...response.results.data as IMedia, isMixed: true };
              showSkeleton.value = false;
            });
          }
        }
      });
    }

    function updateLocation(folderId: string | null) {
      driveStore.appPhotoViewMedia!.folderId = folderId;
    }

    // Cropper functionality
    const image = ref({} as IImageCrop);
    const cropperRef = ref();
    const loader = ref('');

    function openCropper() {
      const fileType = driveStore.appPhotoViewMedia!.name.split('.').pop();
      image.value = {
        url: process.env.VUE_APP_MEDIA_URL + driveStore.appPhotoViewMedia!.fileUrl,
        fileName: driveStore.appPhotoViewMedia!.name,
        fileType: `image/${fileType}`,
      } as IImageCrop;

      cropperRef.value.loader = 'Loading image cropper';
    }

    function handleOnCrop(coordinates: ICropCoordinates) {
      function updateBrowserCache() {
        const photoInPhotoList = driveStore.photos.data.find((photo) => photo.contentId === driveStore.appPhotoViewMedia!.contentId);
        if (photoInPhotoList) {
          clearPhotoCache(photoInPhotoList);
          driveStore.photos.data = [photoInPhotoList, ...driveStore.photos.data.filter((photo) => photo.contentId !== driveStore.appPhotoViewMedia!.contentId)];
        }

        driveStore.cases.data.map((cas) => {
          const photoInCase = cas.photos.find((photo) => photo.contentId === driveStore.appPhotoViewMedia!.contentId);
          if (photoInCase) {
            clearPhotoCache(photoInCase);
            return ({
              ...cas,
              photos: [photoInCase, ...cas.photos.filter((photo) => photo.contentId !== driveStore.appPhotoViewMedia?.contentId)],
            });
          }
          return cas;
        });

        if (driveStore.currentCase) {
          const photoInCase = driveStore.currentCase.photos.find((ph) => ph.contentId === driveStore.appPhotoViewMedia!.contentId);
          if (photoInCase) {
            clearPhotoCache(photoInCase);
            driveStore.currentCase.photos = [photoInCase, ...driveStore.currentCase.photos.filter((photo) => photo.contentId !== driveStore.appPhotoViewMedia!.contentId)];

            driveStore.cases.data = [driveStore.currentCase, ...driveStore.cases.data.filter((cas) => cas.id !== driveStore.currentCase.id)];
          }
        }

        if (driveStore.currentFolder && driveStore.currentFolder.mixedData) {
          const photoInFolder = driveStore.currentFolder.mixedData.find((mix) => mix.file?.contentId === driveStore.appPhotoViewMedia!.contentId);
          if (photoInFolder) {
            clearPhotoCache(photoInFolder.file!);
            // eslint-disable-next-line max-len
            driveStore.currentFolder.mixedData = [photoInFolder, ...driveStore.currentFolder.mixedData.filter((mix) => mix.file?.contentId !== driveStore.appPhotoViewMedia!.contentId)];
          }
        }

        const photoInMixed = driveStore.mixedData.data.find((mix) => mix.file?.contentId === driveStore.appPhotoViewMedia!.contentId);
        if (photoInMixed) {
          clearPhotoCache(photoInMixed.file!);
          driveStore.mixedData.data = [photoInMixed, ...driveStore.mixedData.data.filter((mix) => mix.file?.contentId !== driveStore.appPhotoViewMedia!.contentId)];
        }
      }
      loader.value = 'Saving cropped image';

      UploadService.cropPhoto(driveStore.appPhotoViewMedia!.id, coordinates)
        .then(() => {
          // NotificationService.notifySuccess();
          image.value = {} as IImageCrop;
          updateBrowserCache();
          driveStore.appPhotoViewMedia = undefined;
        }).finally(() => {
          loader.value = '';
        });
    }

    onBeforeMount(() => { isMixedView.value = driveStore.appPhotoViewMedia?.isMixed || false; });

    onMounted(() => {
      setCurrentPage();
    });

    onUnmounted(() => {
      updateMediaOrderContentIds.value.forEach((contentId) => updateMediaOrder(contentId));
    });

    return {
      CURRENT_ROUTE_META,
      isMediaSettingsPopUp,
      currentMedia,
      mediaList,
      route,
      isNetwork,
      isCase,
      openPopUp,
      BOTTOM_POPUP_TYPES,
      MEDIA_TYPES,
      SHARE_TYPES,
      driveStore,
      isPhoto,
      isFile,
      showSkeleton,

      // share popup
      currentItem,
      currentItemType,
      setItemShared,
      setSharedLabels,

      // Cropper functionality
      cropperRef,
      image,
      loader,
      openCropper,
      handleOnCrop,

      // settings
      isMoveToPopUp,
      isSharePopUp,
      isQuestionPopUp,
      fileRenamePopUp,
      openQuestionPopUp,
      openMovePopUp,
      updateLocation,
      deletePhoto,
      saveMediaAsCopy,

      mySwiper,
      initialSlide,
      onSwiper,
      onSlideChange,
      modules: [Mousewheel, Lazy, Navigation],
      onReachEnd,
      currentPage,
      isMixedView,
      openLabelsPopup,

      showAddLabel,
      labelPopup,
      addLabelsOpen,
      cancelLabelsPopup,
      addLabelsClose,
      pauseVideo,
      getPosition,
      renameMountErrorInfo,
    };
  },
});
